import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCheckCircle, FaLightbulb, FaRocket, FaHeadset, FaStar, FaArrowRight, FaUsers, FaQuoteLeft } from 'react-icons/fa';
import './WhyUsPage.css';

const WhyUsPage: React.FC = () => {
    const navigate = useNavigate();
    const [scrollY, setScrollY] = useState(0);

    useEffect(() => {
        const handleScroll = () => setScrollY(window.scrollY);
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const reasons = [
        {
            icon: <FaCheckCircle />,
            title: 'Proven Expertise',
            desc: 'Deep Microsoft technology mastery with senior-level certified consultants across the Netherlands and Europe.',
            highlights: ['Microsoft Certified Experts', 'Senior-Level Consultants', 'Industry Best Practices', 'Enterprise-Grade Delivery'],
            img: '/images/hero_team.png'
        },
        {
            icon: <FaLightbulb />,
            title: 'Innovation-Driven',
            desc: 'We stay ahead of the curve, adopting the latest AI, cloud, and automation technologies to future-proof your business.',
            highlights: ['Cutting-Edge Tech Stack', 'AI & Automation Ready', 'Future-Proof Solutions', 'Continuous R&D Investment'],
            img: '/images/tech_dashboard.png'
        },
        {
            icon: <FaRocket />,
            title: 'Agile Delivery',
            desc: 'Transparent, iterative development with regular demos and milestones that guarantee on-time, on-budget delivery.',
            highlights: ['Full Agile Methodology', 'Rapid Prototyping', 'Iterative Sprints', 'Quality Assurance Built-In'],
            img: '/images/why_us_agile.png'
        },
        {
            icon: <FaHeadset />,
            title: '24/7 Support',
            desc: 'Comprehensive support and maintenance so your systems run flawlessly around the clock — with a dedicated account manager.',
            highlights: ['Round-the-Clock Monitoring', 'Proactive Maintenance', 'Fast Incident Response', 'Dedicated Account Manager'],
            img: '/images/why_us_support.png'
        },
    ];

    const testimonials = [
        {
            quote: 'CoreNuVate delivered our SharePoint migration ahead of schedule with zero disruption to our operations. An outstanding team.',
            author: 'Michiel van den Berg',
            role: 'Chief Technology Officer, Netherlands',
            rating: 5,
            avatar: '/images/avatar_cto.png'
        },
        {
            quote: 'Their Power Platform expertise transformed our reporting. What used to take 3 days now runs automatically every night.',
            author: 'Sophie Bakker',
            role: 'Operations Director, Financial Services',
            rating: 5,
            avatar: '/images/avatar_operations.png'
        },
        {
            quote: 'From day one, they felt like part of our team. The mobile app they built exceeded our expectations on every metric.',
            author: 'Thomas Visser',
            role: 'Head of Product, Retail Sector',
            rating: 5,
            avatar: '/images/avatar_product_head.png'
        },
    ];

    const stats = [
        { num: '100%', label: 'Quality Commitment' },
        { num: '8+', label: 'Services Offered' },
        { num: '15+', label: 'Enterprise Technologies' },
        { num: '24/7', label: 'Support SLA' },
    ];

    return (
        <main className="why-page">
            {/* Banner */}
            <section className="page-banner parallax-wrap">
                <img
                    src="/images/why_us_hero_bg.png"
                    alt="Why Us Background"
                    className="parallax-bg"
                    style={{ transform: `translateY(${scrollY * 0.25}px)` }}
                />
                <div className="parallax-overlay" />
                <div className="container">
                    <span className="page-banner-tag">Why CoreNuVate</span>
                    <h1 className="page-banner-title fade-in-up">
                        Your Trusted Partner for<br /><span className="gradient-text">Digital Transformation</span>
                    </h1>
                    <p className="page-banner-subtitle fade-in-up delay-1">
                        We combine proven expertise, innovative technology, and genuine commitment to client success —
                        making us the partner you can rely on.
                    </p>
                </div>
            </section>

            {/* Metrics */}
            <section className="why-metrics">
                <div className="container">
                    <div className="metrics-strip">
                        {stats.map(s => (
                            <div key={s.label} className="metric-item">
                                <div className="metric-number">{s.num}</div>
                                <div className="metric-label">{s.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Reasons Grid */}
            <section className="section">
                <div className="container">
                    <div className="text-center mb-64">
                        <span className="section-label">Our Differentiators</span>
                        <h2 className="section-title">Why Companies <span>Choose Us</span></h2>
                    </div>
                    <div className="reasons-grid">
                        {reasons.map((r, i) => (
                            <div key={i} className="glass-card reason-card">
                                <div className="reason-img-wrap hover-zoom">
                                    <img src={r.img} alt={r.title} className="reason-img" />
                                    <div className="reason-img-overlay" />
                                </div>
                                <div className="reason-content">
                                    <div className="reason-top">
                                        <div className="icon-box">{r.icon}</div>
                                        <h3>{r.title}</h3>
                                    </div>
                                    <p className="reason-desc">{r.desc}</p>
                                    <ul className="reason-highlights">
                                        {r.highlights.map(h => (
                                            <li key={h}><span className="check-dot-green" />{h}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section className="section why-testimonials">
                <div className="container">
                    <div className="text-center mb-64">
                        <span className="section-label">Client Voices</span>
                        <h2 className="section-title">What Our Clients <span>Say</span></h2>
                    </div>
                    <div className="testimonials-grid">
                        {testimonials.map((t, i) => (
                            <div key={i} className="glass-card testimonial-card">
                                <div className="testimonial-header">
                                    <FaQuoteLeft className="quote-icon" />
                                    <div className="stars">
                                        {[...Array(t.rating)].map((_, s) => <FaStar key={s} />)}
                                    </div>
                                </div>
                                <p className="testimonial-quote">"{t.quote}"</p>
                                <div className="testimonial-author">
                                    <div className="author-img">
                                        <img src={t.avatar} alt={t.author} />
                                    </div>
                                    <div>
                                        <div className="author-name">{t.author}</div>
                                        <div className="author-role">{t.role}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="section">
                <div className="container">
                    <div className="cta-banner">
                        <h2>Ready to Experience the<br /><span className="gradient-text">CoreNuVate Difference?</span></h2>
                        <p>Partner with a team of certified experts who are committed to accelerating your digital transformation.</p>
                        <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
                            <button className="btn-primary" onClick={() => navigate('/contact')}>
                                <FaArrowRight /> Get in Touch
                            </button>
                            <button className="btn-secondary" onClick={() => navigate('/engagement')}>
                                View Engagement Models
                            </button>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default WhyUsPage;
